import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { Check2, XLg } from 'react-bootstrap-icons';
import { Form as RouterForm, useFetcher, useNavigate } from 'react-router-dom';

import { ModalWarning } from '@/components/modal-warning';
import { ReportEmail } from '@/helper/interfaces';

interface Properties {
  type: string;
  redirectTo: string;
  disabled?: boolean;
  showDelete?: boolean;
  showPrint?: boolean;
  showEmail?: boolean;
  emailInfo?: ReportEmail[];
  children: React.ReactNode;
}

export function FormButtons({
  type,
  redirectTo,
  showDelete = true,
  showPrint = false,
  showEmail = false,
  emailInfo,
  children
}: Properties) {
  const [modalDelete, setModalDelete] = React.useState(false);
  const handleOpenModalDelete = () => setModalDelete(true);
  const handleCloseModalDelete = () => setModalDelete(false);

  const [modalEmail, setModalEmail] = React.useState(false);
  const handleOpenModalEmail = () => setModalEmail(true);
  const handleCloseModalEmail = () => setModalEmail(false);

  const [sendCopy, setSendCopy] = React.useState(true);

  const navigate = useNavigate();
  function handleBack(event: React.FormEvent) {
    event.preventDefault();
    navigate(redirectTo);
  }

  const fetcher = useFetcher();
  const [isLoading, setIsLoading] = React.useState(false);
  const modalError = React.useRef(null);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [loadingMessage, setLoadingMessage] = React.useState('');
  React.useEffect(() => {
    if (fetcher.state == 'loading' || fetcher.state == 'submitting') {
      setIsLoading(true);
    } else {
      setIsLoading(false);
      if (fetcher.data?.status == 500) {
        setErrorMessage(fetcher.data?.message);
        modalError.current.openModal();
      }
    }
  }, [fetcher]);

  function handlePrint() {
    setLoadingMessage('The PDF file is exporting...');
    // eslint-disable-next-line unicorn/no-null
    fetcher.submit(null, {
      method: 'post',
      action: 'print'
    });
  }

  function handleEmail() {
    setModalEmail(false);
    setLoadingMessage('Sending email...');
    fetcher.submit(
      { sendCopy: sendCopy },
      {
        method: 'post',
        action: 'email'
      }
    );
  }

  return (
    <Row className='mt-3 mb-3'>
      <Col className='col-12 text-end'>
        {showDelete && (
          <Button variant='danger' size='sm' className='me-2' type='button' onClick={handleOpenModalDelete}>
            Delete
          </Button>
        )}
        {showPrint && (
          <Button variant='primary' size='sm' className='me-2' type='button' onClick={handlePrint}>
            Print
          </Button>
        )}
        {showEmail && (
          <Button variant='primary' size='sm' className='me-2' type='button' onClick={handleOpenModalEmail}>
            Email
          </Button>
        )}
        <Button variant='secondary' size='sm' type='button' onClick={handleBack}>
          Go Back
        </Button>
      </Col>
      <Modal show={modalDelete} onHide={handleCloseModalDelete}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {type}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>{children}</Modal.Body>
        <Modal.Footer>
          <Form as={RouterForm} noValidate method='delete'>
            <Form.Control type='hidden' name='redirectTo' value={redirectTo} />
            <Button variant='secondary' type='button' className='me-2' onClick={handleCloseModalDelete}>
              Cancel
            </Button>
            <Button variant='danger' type='submit'>
              Delete
            </Button>
          </Form>
        </Modal.Footer>
      </Modal>
      <Modal show={modalEmail} onHide={handleCloseModalEmail}>
        <Modal.Header closeButton>
          <Modal.Title>Send Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to email this report?</p>
          <Form.Check
            type='checkbox'
            id='sendCopy'
            label='Send Myself A Copy'
            checked={sendCopy}
            onChange={() => setSendCopy(!sendCopy)}
          />
          {emailInfo?.length > 0 && (
            <React.Fragment>
              <hr />
              <p>This report has been sent before:</p>
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Sent At</th>
                    <th>By</th>
                    <th>Copy Sent?</th>
                  </tr>
                </thead>
                <tbody>
                  {emailInfo.map((info) => (
                    <tr key={info.id}>
                      <td>{format(parseISO(info.emailedAt), 'MM/dd/yyyy hh:mm a')}</td>
                      <td>{info.emailedBy.name}</td>
                      <td className='text-center'>{info.sentCopy ? <Check2 /> : <XLg />}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </React.Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' type='button' className='me-2' onClick={handleCloseModalEmail}>
            Cancel
          </Button>
          <Button variant='primary' type='button' onClick={handleEmail}>
            Send Email
          </Button>
        </Modal.Footer>
      </Modal>
      <ModalWarning title='Error' ref={modalError}>
        {errorMessage}
      </ModalWarning>
      <Modal show={isLoading} backdrop='static' keyboard={false}>
        <Modal.Body className='text-center'>
          {loadingMessage}
          <br />
          <br />
          <Spinner />
          <br />
        </Modal.Body>
      </Modal>
    </Row>
  );
}
